import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useAppState } from '../hooks/useAppState';
import { localStorageUtils } from '../utils/localStorage';
import { LocationInput, WheelchairSpecs } from '../types';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import AddressSearchInput from '../components/AddressSearchInput';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { backendApi } from '../services/backendApi';

interface GeneratePageProps {
  onRouteGenerated: (
    startLocation: LocationInput,
    endLocation: LocationInput,
    routeCoordinates?: [number, number][],
    requestId?: number,
    routeStats?: {
      total_distance?: number;
      total_crossings?: number;
      distance_sidewalk?: number;
      distance_bikelane?: number;
      total_duration?: number;
      time_sidewalk?: number;
      time_bikelane?: number;
    }
  ) => void;
  onBackToOnboarding?: () => void;
}

const GeneratePage: React.FC<GeneratePageProps> = ({ onRouteGenerated, onBackToOnboarding }) => {
  const { t } = useTranslation();
  const { state } = useAppState();
  const [selectedWheelchair, setSelectedWheelchair] = useState<WheelchairSpecs | null>(
    state.userPreferences.wheelchairSpecs.find((spec) => spec.isDefault) ||
      state.userPreferences.wheelchairSpecs[0] ||
      null
  );
  const [showWheelchairDropdown, setShowWheelchairDropdown] = useState(false);

  const handleWheelchairSelect = (wheelchair: WheelchairSpecs) => {
    setSelectedWheelchair(wheelchair);
    setShowWheelchairDropdown(false);
  };

  const [startLocation, setStartLocation] = useState<LocationInput>({ address: '' });
  const [endLocation, setEndLocation] = useState<LocationInput>({ address: '' });
  const [isGeneratingRoute, setIsGeneratingRoute] = useState(false);
  const [showRouteUnavailable, setShowRouteUnavailable] = useState(false);

  const handleGenerateRoute = async () => {
    if (!startLocation.address || !endLocation.address) {
      return;
    }

    if (!startLocation.coordinates || !endLocation.coordinates) {
      console.error('Missing coordinates for route generation');
      setShowRouteUnavailable(true);
      return;
    }

    const userId = localStorageUtils.getUserId();
    if (!userId) {
      console.warn('No user ID found; cannot request route from backend');
      setShowRouteUnavailable(true);
      return;
    }

    setIsGeneratingRoute(true);
    try {
      const backendResponse = await backendApi.initRoute(userId, {
        start_lat: startLocation.coordinates.lat,
        start_lon: startLocation.coordinates.lng,
        end_lat: endLocation.coordinates.lat,
        end_lon: endLocation.coordinates.lng,
        start_address: startLocation.address,
        end_address: endLocation.address,
      });

      let routeCoordinates: [number, number][] | undefined = undefined;
      let requestId: number | undefined = undefined;
      const routeStats = {
        total_distance: backendResponse?.total_distance,
        total_crossings: backendResponse?.total_crossings,
        distance_sidewalk: backendResponse?.distance_sidewalk,
        distance_bikelane: backendResponse?.distance_bikelane,
        total_duration: backendResponse?.total_duration,
        time_sidewalk: backendResponse?.time_sidewalk,
        time_bikelane: backendResponse?.time_bikelane,
      };

      if (backendResponse?.request_id !== undefined) {
        requestId = backendResponse.request_id;
      }

      if (backendResponse?.result?.geometry?.coordinates) {
        const geoJsonCoords = backendResponse.result.geometry.coordinates;
        routeCoordinates = geoJsonCoords.map((coord: [number, number]) => [coord[1], coord[0]]);
      } else if (backendResponse?.coordinates) {
        routeCoordinates = backendResponse.coordinates;
      } else if (backendResponse?.route?.coordinates) {
        routeCoordinates = backendResponse.route.coordinates;
      }

      if (routeCoordinates && routeCoordinates.length > 0) {
        onRouteGenerated(startLocation, endLocation, routeCoordinates, requestId, routeStats);
        return;
      }

      setShowRouteUnavailable(true);
    } catch (backendError) {
      console.warn('Backend route generation failed:', backendError);
      setShowRouteUnavailable(true);
    } finally {
      setIsGeneratingRoute(false);
    }
  };

  const handleBackToOnboarding = () => {
    localStorageUtils.resetOnboarding();
    onBackToOnboarding?.();
  };

  return (
    <div className="generate-page">
      <LanguageSwitcher />
      <div className="generate-content">
        {onBackToOnboarding && (
          <div className="back-button-container">
            <button
              className="back-button"
              onClick={handleBackToOnboarding}
              disabled={isGeneratingRoute}
              title={t('generate.backToOnboarding')}
            >
              <ArrowLeft size={20} />
              <span>{t('generate.back')}</span>
            </button>
          </div>
        )}

        <main className="generate-main">
          <section className="wheelchair-section">
            <label className="section-label">{t('generate.wheelchair.title')}</label>
            <div className="dropdown-container">
              <button
                type="button"
                className="dropdown-button wheelchair-dropdown"
                onClick={() => setShowWheelchairDropdown(!showWheelchairDropdown)}
                aria-expanded={showWheelchairDropdown}
              >
                <span>
                  {selectedWheelchair
                    ? `${selectedWheelchair.brand} ${selectedWheelchair.type} ${t('wheelchair.label')}`
                    : t('onboarding.wheelchair.select')}
                </span>
                <ChevronDown size={16} />
              </button>

              {showWheelchairDropdown && (
                <ul className="dropdown-list">
                  {state.userPreferences.wheelchairSpecs.map((wheelchair) => (
                    <li
                      key={wheelchair.id}
                      className="dropdown-item"
                      onClick={() => handleWheelchairSelect(wheelchair)}
                    >
                      <div className="wheelchair-option">
                        <span className="wheelchair-name">
                          {wheelchair.brand} {wheelchair.type} {t('wheelchair.label')}
                        </span>
                        <span className="wheelchair-dimensions">
                          {wheelchair.width}cm × {wheelchair.length}cm
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="location-section">
            <label className="section-label">{t('generate.whereTo.title')}</label>

            <div className="location-inputs">
              <AddressSearchInput
                placeholder={t('generate.location.label')}
                value={startLocation.address}
                onChange={(result) =>
                  setStartLocation({
                    address: result.address,
                    coordinates: result.coordinates,
                  })
                }
                onInputChange={(value) => setStartLocation((prev) => ({ ...prev, address: value }))}
                className="start-location-input"
              />

              <div className="location-connector"></div>

              <AddressSearchInput
                placeholder={t('generate.destination.placeholder')}
                value={endLocation.address}
                onChange={(result) =>
                  setEndLocation({
                    address: result.address,
                    coordinates: result.coordinates,
                  })
                }
                onInputChange={(value) => setEndLocation((prev) => ({ ...prev, address: value }))}
                className="destination-input"
              />
            </div>
          </section>

          <section className="generate-section">
            <button
              className="btn btn-primary btn-large generate-button"
              onClick={handleGenerateRoute}
              disabled={!startLocation.address || !endLocation.address || isGeneratingRoute}
            >
              {isGeneratingRoute ? t('status.calculating') || 'Calculating...' : t('generate.button')}
            </button>
          </section>
        </main>
      </div>

      {showRouteUnavailable && (
        <div
          className="feedback-modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-describedby="route-unavailable-desc"
        >
          <div className="feedback-modal">
            <div style={{ padding: '1.5rem' }}>
              <p
                id="route-unavailable-desc"
                style={{ margin: '0 0 1.25rem', color: '#333', lineHeight: 1.5 }}
              >
                {t('route.unavailable')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowRouteUnavailable(false)}
              >
                {t('button.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratePage;
